// src/components/ChatAgent.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import { useChat } from '../contexts/ChatContext.jsx';
import { showToast } from '../utils/toast';
import { isClientUser, readStoredUser } from '../utils/userFlags.js';
import './ChatAgent.css';
const STATE_KEY = 'chatAgentState';
const AGENT_ANNOUNCEMENT_VERSION = 'v1';
const AGENT_ANNOUNCEMENT_PENDING_KEY = `chat.agent.announcement.${AGENT_ANNOUNCEMENT_VERSION}.pending`;
const CHAT_WINDOW_ANIMATION_MS = 900;

function getAgentAnnouncementShownKey(userId) {
  return `chat.agent.announcement.${AGENT_ANNOUNCEMENT_VERSION}.shown.${userId}`;
}

function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }
function clampState(saved) {
  const vpW = window.innerWidth || 1024;
  const vpH = window.innerHeight || 768;
  const minW = 300, minH = 400, maxH = 1000;
  const width = clamp(Number(saved?.width ?? 400), minW, vpW);
  const height = clamp(Number(saved?.height ?? 600), minH, Math.min(maxH, vpH));
  const maxX = Math.max(0, vpW - width);
  const maxY = Math.max(0, vpH - height);
  const x = clamp(Number(saved?.x ?? 250), 0, maxX);
  const y = clamp(Number(saved?.y ?? 50), 0, maxY);
  return { width, height, x, y };
}

function normalizeAgentCommand(message) {
  return String(message || '')
    .toLowerCase()
    .replace(/[^\w\s']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getReceiptEditNavigationDetail(to) {
  try {
    const url = new URL(String(to || ''), window.location.origin);
    const receiptId = Number(url.searchParams.get('receiptid') || 0) || 0;
    const agentEdit = String(url.searchParams.get('agent_edit') || '') === '1';
    if (url.pathname !== '/receipts' || !receiptId || !agentEdit) return null;
    return { receiptId };
  } catch {
    return null;
  }
}

function navigateWithAgentSideEffects(navigate, to) {
  navigate(to);
  const receiptEditDetail = getReceiptEditNavigationDetail(to);
  if (receiptEditDetail) {
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('agent:editReceipt', { detail: receiptEditDetail }));
    }, 120);
  }
}

async function logLocalAgentExchange(apiBase, userId, userMessage, assistantReply, meta = null) {
  if (!userId) return false;
  const base = String(apiBase || '').trim();
  const url = `${base}/api/agent/log`;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 1800);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': String(userId),
      },
      credentials: 'include',
      body: JSON.stringify({
        userMessage,
        assistantReply,
        model: 'assistant-local-navigation',
        meta,
      }),
      keepalive: true,
      signal: controller.signal,
    });
    if (!res.ok) {
      const errorText = await res.text().catch(() => '');
      console.warn('[ChatAgent] local agent log failed:', res.status, errorText || res.statusText);
      return false;
    }
    return true;
  } catch (error) {
    console.warn('[ChatAgent] local agent log error:', error?.message || error);
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function buildLocalAgentNavigationIntent(message) {
  const s = normalizeAgentCommand(message);
  if (!s) return null;
  if (/\b(write|draft|compose|rewrite|craft)\b/.test(s) && /\bemail\b/.test(s)) return null;

  const raw = String(message || '').trim();
  const receiptMatch = raw.match(/^(?:please\s+)?(?:open|edit|go to|goto|show|view|take me to|bring up|pull up|load|navigate|display)\s+(?:warehouse\s+)?(?:receipt|wr)\s*#?\s*(\d+)\b/i);
  if (receiptMatch?.[1]) {
    const receiptId = Number(receiptMatch[1]);
    if (Number.isFinite(receiptId) && receiptId > 0) {
      const params = new URLSearchParams();
      params.set('receiptid', String(receiptId));
      params.set('agent_edit', '1');
      return {
        reply: `Opening Receipt #${receiptId} for editing.`,
        to: `/receipts?${params.toString()}`,
      };
    }
  }

  return null;
}

/* ======================= Sortable/Paginated Table ======================= */
function TableView({ table }) {
  const [sortIndex, setSortIndex] = useState(-1);
  const [sortDir, setSortDir] = useState('asc');
  const [rows, setRows] = useState(table?.rows || []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setRows(table?.rows || []);
    setSortIndex(-1);
    setSortDir('asc');
    setPage(1);
  }, [table]);

  const sortBy = (idx) => {
    let dir = sortDir;
    if (sortIndex === idx) {
      dir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(dir);
    } else {
      setSortIndex(idx);
      dir = 'asc';
      setSortDir(dir);
    }
    const header = table.headers[idx] || '';
    const isDate = /date/i.test(header);
    const newRows = [...rows].sort((a, b) => {
      let av = a[idx], bv = b[idx];
      if (isDate) {
        const ad = av ? Date.parse(av) : 0;
        const bd = bv ? Date.parse(bv) : 0;
        return dir === 'asc' ? ad - bd : bd - ad;
      }
      const an = Number(av), bn = Number(bv);
      const aIsNum = !Number.isNaN(an) && String(av ?? '').trim() !== '';
      const bIsNum = !Number.isNaN(bn) && String(bv ?? '').trim() !== '';
      if (aIsNum && bIsNum) return dir === 'asc' ? an - bn : bn - an;
      return dir === 'asc'
        ? String(av ?? '').localeCompare(String(bv ?? ''))
        : String(bv ?? '').localeCompare(String(av ?? ''));
    });
    setRows(newRows);
    setPage(1);
  };

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);
  const changePage = (next) => setPage(p => clamp(p + next, 1, totalPages));
  const goToPage = (p) => setPage(clamp(Number(p) || 1, 1, totalPages));

  if (!table || !Array.isArray(table.headers) || !Array.isArray(table.rows)) return null;
  return (
    <div className="chat-table-wrap">
      {table.title ? <div className="chat-table-title">{table.title}</div> : null}
      <div className="chat-table-scroll">
        <table className="chat-table">
          <thead>
            <tr>
              {table.headers.map((h, i) => (
                <th
                  key={i}
                  onClick={() => sortBy(i)}
                  style={{ cursor: 'pointer', whiteSpace: 'normal', wordWrap: 'break-word' }}
                  title="Click to sort"
                >
                  {h}
                  {sortIndex === i ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, rIdx) => (
              <tr key={`${page}-${rIdx}`}>
                {table.headers.map((_, cIdx) => (
                  <td key={cIdx} style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    {row[cIdx] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="chat-table-pagination">
        <button onClick={() => changePage(-1)} disabled={page <= 1}>Prev</button>
        <span>Page</span>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={page}
          onChange={(e) => goToPage(e.target.value)}
        />
        <span>of {totalPages}</span>
        <button onClick={() => changePage(1)} disabled={page >= totalPages}>Next</button>
        <span className="sep" />
        <label>
          Rows per page:&nbsp;
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <span className="summary">
          &nbsp;•&nbsp;Showing {Math.min(total, start + 1)}–{Math.min(total, start + pageSize)} of {total}
        </span>
      </div>
    </div>
  );
}

/* =============================== Chat Agent =============================== */
const ChatAgent = () => {
  const navigate = useNavigate();
  const { isChatOpen, setIsChatOpen, assistantAnchorRect, setAssistantNeedsAttention } = useChat();
  // Restrict to internal users only
  const isClient = (() => {
    try {
      return isClientUser(readStoredUser());
    } catch { return false; }
  })();
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [shouldRender, setShouldRender] = useState(isChatOpen && !isClient);
  const [isWindowVisible, setIsWindowVisible] = useState(isChatOpen && !isClient);
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem('chatAgentMode') || 'agent'; } catch { return 'agent'; }
  });
  useEffect(() => { try { localStorage.setItem('chatAgentMode', mode); } catch {} }, [mode]);
  const [dmList, setDmList] = useState([]);
  const [online, setOnline] = useState([]); // [{userid, fullname, login}]
  const [dmWith, setDmWith] = useState(null); // userid
  const [dmMsgs, setDmMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [userId, setUserId] = useState(0);
  const messagesRef = useRef(null);
  const hydratedRef = useRef(false);
  const sendMessageRef = useRef(null);
  const skipNextHistoryHydrationRef = useRef(false);
  const currentRequestControllerRef = useRef(null);
  const [size, setSize] = useState({ width: 400, height: 600 });
  const [position, setPosition] = useState({ x: 250, y: 50 });

  // ---- Dictation (Web Speech API) ----
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const mediaMimeTypeRef = useRef('audio/webm');
  const submitAfterMediaStopRef = useRef(true);
  const mediaAnalyserRef = useRef(null);
  const mediaSourceNodeRef = useRef(null);
  const mediaMonitorIntervalRef = useRef(null);
  const [interim, setInterim] = useState(''); // live preview
  const [isTranscribing, setIsTranscribing] = useState(false);
  const sendingGuardRef = useRef(false); // block duplicate sends
  const heardSpeechRef = useRef(false); // only auto-send if true
  const autoStoppedDictationRef = useRef(false);
  const audioContextRef = useRef(null);

  // Input caret/scroll-to-end
  const inputElRef = useRef(null);
  const scrollInputToEnd = useCallback(() => {
    const el = inputElRef.current;
    if (!el) return;
    const len = el.value.length;
    try { el.selectionStart = len; el.selectionEnd = len; } catch {}
    el.scrollLeft = el.scrollWidth;
  }, []);
  const focusInput = useCallback(() => {
    const el = inputElRef.current;
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch { try { el.focus(); } catch {} }
    const len = el.value.length;
    try { el.selectionStart = len; el.selectionEnd = len; } catch {}
  }, []);

  useEffect(() => {
    const t = setTimeout(scrollInputToEnd, 0);
    return () => clearTimeout(t);
  }, [input, interim, scrollInputToEnd]);

  useEffect(() => {
    if (!shouldRender || !isWindowVisible) return;
    const frameId = window.requestAnimationFrame(() => {
      focusInput();
      scrollInputToEnd();
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [shouldRender, isWindowVisible, focusInput, scrollInputToEnd]);

  // Latest state mirrors for recognition callbacks
  const inputStateRef = useRef('');
  const interimStateRef = useRef('');
  useEffect(() => { inputStateRef.current = input; }, [input]);
  useEffect(() => { interimStateRef.current = interim; }, [interim]);

  const getAudioContext = useCallback(() => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new AudioCtx();
      } catch {
        return null;
      }
    }
    return audioContextRef.current;
  }, []);

  const stopMediaStream = useCallback(() => {
    if (mediaMonitorIntervalRef.current) {
      clearInterval(mediaMonitorIntervalRef.current);
      mediaMonitorIntervalRef.current = null;
    }
    try { mediaSourceNodeRef.current?.disconnect?.(); } catch {}
    mediaSourceNodeRef.current = null;
    mediaAnalyserRef.current = null;
    try {
      const stream = mediaStreamRef.current;
      if (stream) {
        for (const track of stream.getTracks()) {
          try { track.stop(); } catch {}
        }
      }
    } catch {}
    mediaStreamRef.current = null;
  }, []);

  const playTone = useCallback(async (kind = 'start') => {
    try {
      const ctx = getAudioContext();
      if (!ctx) return;
      if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch {}
      }
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';

      if (kind === 'start') {
        osc.frequency.setValueAtTime(740, now);
        osc.frequency.exponentialRampToValueAtTime(980, now + 0.06);
      } else {
        osc.frequency.setValueAtTime(880, now);
        osc.frequency.exponentialRampToValueAtTime(620, now + 0.07);
      }

      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.03, now + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + (kind === 'start' ? 0.09 : 0.1));

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + (kind === 'start' ? 0.1 : 0.11));
    } catch {}
  }, [getAudioContext]);

  // Silence detection
  const SILENCE_MS = 1200;
  const silenceTimerRef = useRef(null);
  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);
  const finalizeDictationAfterSilence = useCallback(() => {
    if (mediaRecorderRef.current) {
      clearSilenceTimer();
      setIsListening(false);
      setInterim('');
      submitAfterMediaStopRef.current = true;
      try { mediaRecorderRef.current.stop(); } catch {}
      return;
    }
    const committed = inputStateRef.current || '';
    const preview = interimStateRef.current || '';
    const composed = `${committed}${preview ? (committed && !committed.endsWith(' ') ? ' ' : '') + preview : ''}`.trim();
    clearSilenceTimer();
    autoStoppedDictationRef.current = true;
    setIsListening(false);
    setInterim('');
    try { recognitionRef.current && recognitionRef.current.stop(); } catch {}
    playTone('stop');
    if (heardSpeechRef.current && composed && !sendingGuardRef.current) {
      if (sendMessageRef.current) sendMessageRef.current(composed);
    }
  }, [clearSilenceTimer, playTone]);
  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      finalizeDictationAfterSilence();
    }, SILENCE_MS);
  }, [clearSilenceTimer, finalizeDictationAfterSilence]);

  const startMediaLevelMonitor = useCallback((stream) => {
    if (!stream) return;
    if (mediaMonitorIntervalRef.current) {
      clearInterval(mediaMonitorIntervalRef.current);
      mediaMonitorIntervalRef.current = null;
    }
    try {
      const ctx = getAudioContext();
      if (!ctx) return;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.2;
      source.connect(analyser);
      mediaSourceNodeRef.current = source;
      mediaAnalyserRef.current = analyser;
      const data = new Uint8Array(analyser.fftSize);
      const threshold = 0.02;
      mediaMonitorIntervalRef.current = window.setInterval(() => {
        const activeAnalyser = mediaAnalyserRef.current;
        if (!activeAnalyser || !mediaRecorderRef.current) return;
        activeAnalyser.getByteTimeDomainData(data);
        let sumSquares = 0;
        for (let i = 0; i < data.length; i += 1) {
          const normalized = (data[i] - 128) / 128;
          sumSquares += normalized * normalized;
        }
        const rms = Math.sqrt(sumSquares / data.length);
        if (rms > threshold) {
          heardSpeechRef.current = true;
          armSilenceTimer();
        }
      }, 100);
    } catch (err) {
      console.warn('Audio level monitor failed', err);
    }
  }, [armSilenceTimer, getAudioContext]);

  useEffect(() => {
    return () => {
      clearSilenceTimer();
      try { recognitionRef.current?.stop?.(); } catch {}
      try { mediaRecorderRef.current?.stop?.(); } catch {}
      stopMediaStream();
    };
  }, [clearSilenceTimer, stopMediaStream]);

  // Load user id
  useEffect(() => {
    try {
      const user = readStoredUser();
      setUserId(Number(user?.userid || 0) || 0);
    } catch { setUserId(0); }
  }, []);

  // Hydrate size/pos
  useEffect(() => {
    if (!isChatOpen || hydratedRef.current) return;
    try {
      const raw = localStorage.getItem(STATE_KEY);
      const c = clampState(raw ? JSON.parse(raw) : {});
      setSize({ width: c.width, height: c.height });
      setPosition({ x: c.x, y: c.y });
    } catch {}
    hydratedRef.current = true;
  }, [isChatOpen]);

  // Persist size/pos
  useEffect(() => {
    if (!hydratedRef.current) return;
    localStorage.setItem(STATE_KEY, JSON.stringify({ ...size, ...position }));
  }, [size, position]);

  // Auto-scroll messages
  const scrollToBottom = useCallback(() => {
    if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, isSending, scrollToBottom]);
  useEffect(() => {
    const onResize = () => scrollToBottom();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [scrollToBottom]);

  useEffect(() => {
    if (isClient) {
      setShouldRender(false);
      setIsWindowVisible(false);
      return undefined;
    }

    let frameId = 0;
    let timeoutId = 0;

    if (isChatOpen) {
      setShouldRender(true);
      frameId = window.requestAnimationFrame(() => setIsWindowVisible(true));
    } else {
      setIsWindowVisible(false);
      timeoutId = window.setTimeout(() => setShouldRender(false), CHAT_WINDOW_ANIMATION_MS);
    }

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [isChatOpen, isClient]);

  // Load agent chat history or messages based on mode
  useEffect(() => {
    if (!isChatOpen || !userId) return;
    if (mode === 'agent') {
      if (skipNextHistoryHydrationRef.current) {
        skipNextHistoryHydrationRef.current = false;
        setMessages([]);
        return;
      }
      (async () => {
        try {
          const res = await fetch(`${import.meta.env.VITE_API_URL}/api/agent/history?limit=150`, { headers: { 'x-user-id': String(userId) } });
          if (!res.ok) return;
          const j = await res.json();
          if (Array.isArray(j.data) && j.data.length) setMessages(j.data);
        } catch {}
      })();
    } else {
      (async () => {
        try {
          const res = await fetch(`${import.meta.env.VITE_API_URL}/api/messages/conversations`);
          if (res.ok) setDmList(await res.json());
        } catch {}
        try {
          const r2 = await fetch(`${import.meta.env.VITE_API_URL}/api/messages/online`);
          const j2 = await r2.json().catch(()=>({ online: [] }));
          if (Array.isArray(j2?.online)) setOnline(j2.online);
        } catch {}
      })();
    }
  }, [isChatOpen, userId, mode]);

  useEffect(() => {
    if (!userId || isClient) return;
    let isPending = false;
    try {
      isPending = sessionStorage.getItem(AGENT_ANNOUNCEMENT_PENDING_KEY) === '1';
    } catch {}
    if (!isPending) return;

    try { sessionStorage.removeItem(AGENT_ANNOUNCEMENT_PENDING_KEY); } catch {}

    let hasSeen = false;
    try {
      hasSeen = localStorage.getItem(getAgentAnnouncementShownKey(userId)) === '1';
    } catch {}
    if (hasSeen) return;

    setAnnouncement(
      'Welcome to the Easy3PL Assistant.\n\nI can help you find orders, e-orders, invoices, inventory, client summaries, low-stock items, and recent activity. I can also open pages for you with filters applied.\n\nTry:\nshow me all your orders\nshow me e-orders for today\nshow open invoices\nshow me the items for the client CP Intermodal Logistics\nopen invoice for AMC Promotions\nlook up sku ABC-123\nwhat changed in the last 2 days\nhelp'
    );
    setMessages([]);
    skipNextHistoryHydrationRef.current = true;
    setAssistantNeedsAttention(true);
    setIsChatOpen(true);
    try {
      localStorage.setItem(getAgentAnnouncementShownKey(userId), '1');
    } catch {}
  }, [isClient, setAssistantNeedsAttention, setIsChatOpen, userId]);

  // SSE subscription for presence + live messages (DMs)
  useEffect(() => {
    if (!isChatOpen || mode !== 'dm') return;
    const src = new EventSource(`${import.meta.env.VITE_API_URL}/api/messages/stream`, { withCredentials: true });
    src.addEventListener('presence', (e) => {
      try { const d = JSON.parse(e.data || '{}'); if (Array.isArray(d.online)) setOnline(d.online); } catch {}
    });
    src.addEventListener('message', (e) => {
      try {
        const m = JSON.parse(e.data || '{}');
        // If this message is for the active thread, append
        if (m && ((m.sender_id === userId && m.recipient_id === dmWith) || (m.sender_id === dmWith && m.recipient_id === userId))) {
          setDmMsgs(prev => [...prev, m]);
        }
        // Update conversations list ordering by bumping counterpart to top if needed
        const otherId = (m.sender_id === userId) ? m.recipient_id : m.sender_id;
        setDmList(prev => {
          const exists = prev.find(x => x.userid === otherId);
          const others = prev.filter(x => x.userid !== otherId);
          const basic = exists || online.find(x => x.userid === otherId) || { userid: otherId, fullname: `User #${otherId}`, login: '' };
          return [basic, ...others];
        });
      } catch {}
    });
    src.onerror = () => { /* auto-reconnect by browser */ };
    return () => { try { src.close(); } catch {} };
  }, [isChatOpen, mode, dmWith, userId]);

  /* ---------------------- sendMessage ---------------------- */
  const sendMessage = useCallback(async (overrideText) => {
    if (sendingGuardRef.current) return;
    const composed = (overrideText != null ? overrideText :
      `${input}${interim ? (input && !input.endsWith(' ') ? ' ' : '') + interim : ''}`
    ).trim();
    if (!composed || !userId) {
      console.log('[ChatAgent] Send aborted: composed=', composed, 'userId=', userId);
      return;
    }
    sendingGuardRef.current = true;
    if (recognitionRef.current && isListening) {
      clearSilenceTimer();
      setIsListening(false);
      try { recognitionRef.current.stop(); } catch {}
    }
    if (mediaRecorderRef.current && isListening) {
      try {
        submitAfterMediaStopRef.current = true;
        mediaRecorderRef.current.stop();
      } catch {}
      sendingGuardRef.current = false;
      return;
    }
    setAnnouncement('');
    setIsSending(true);
    const newMessages = [...messages, { text: composed, sender: 'user' }];
    setMessages(newMessages);
    setInput('');
    setInterim('');
    try {
      const apiBase = import.meta.env.VITE_API_URL || '';
      const localNavigationIntent = buildLocalAgentNavigationIntent(composed);
      if (localNavigationIntent) {
        const botMsg = { text: localNavigationIntent.reply, sender: 'bot' };
        setMessages([...newMessages, botMsg]);
        await logLocalAgentExchange(apiBase, userId, composed, localNavigationIntent.reply, {
          kind: 'local-navigation',
          to: localNavigationIntent.to,
        });
        navigateWithAgentSideEffects(navigate, localNavigationIntent.to);
        return;
      }

      const res = await fetch(`${apiBase}/api/agent/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': String(userId) },
        body: JSON.stringify({
          message: composed,
          currentPath: `${window.location.pathname || ''}${window.location.search || ''}`,
        }),
        signal: (() => {
          const controller = new AbortController();
          currentRequestControllerRef.current = controller;
          return controller.signal;
        })(),
      });
      const data = await res.json();
      currentRequestControllerRef.current = null;
      console.log('[ChatAgent] API response:', JSON.stringify(data, null, 2));
      if (res.ok) {
        // Navigate
        if (data.action && data.action.type === 'navigate' && data.action.to) {
          const botMsg = { text: data.reply || 'OK', sender: 'bot' };
          setMessages([...newMessages, botMsg]);
          navigateWithAgentSideEffects(navigate, data.action.to);
          sendingGuardRef.current = false;
          return;
        }
        // Create Receipt
        if (data.action && data.action.type === 'createReceipt' && data.action.payload) {
          const botMsg = { text: data.reply || 'OK', sender: 'bot' };
          setMessages([...newMessages, botMsg]);
          navigate('/receipts');
          setTimeout(() => {
            const payload = data.action.payload;
            console.log('[ChatAgent] Dispatching agent:createReceipt:', {
              userId,
              warehouseId: payload.warehouseid,
              clientId: payload.clientid,
              warehouseName: payload.resolvedNames?.warehouse || payload.warehouseName || '',
              clientName: payload.resolvedNames?.client || payload.clientName || '',
              po: payload.po || '',
              entry: payload.entry || '',
              notes: payload.notes || ''
            });
            window.dispatchEvent(new CustomEvent('agent:createReceipt', {
              detail: {
                userId,
                warehouseId: payload.warehouseid || null,
                clientId: payload.clientid || null,
                warehouseName: payload.resolvedNames?.warehouse || payload.warehouseName || '',
                clientName: payload.resolvedNames?.client || payload.clientName || '',
                po: payload.po || '',
                entry: payload.entry || '',
                notes: payload.notes || ''
              }
            }));
          }, 120);
          sendingGuardRef.current = false;
          return;
        }
        // Create Order
        if (data.action && data.action.type === 'createOrder' && data.action.payload) {
          const botMsg = { text: data.reply || 'OK', sender: 'bot' };
          setMessages([...newMessages, botMsg]);
          navigate('/orders');
          setTimeout(() => {
            const payload = data.action.payload;
            console.log('[ChatAgent] Dispatching agent:createOrder:', {
              userId,
              warehouseId: payload.warehouseid,
              clientId: payload.clientid,
              orderdate: payload.orderdate,
              po: payload.po || '',
              notes: payload.notes || ''
            });
            window.dispatchEvent(new CustomEvent('agent:createOrder', {
              detail: {
                userId,
                warehouseId: payload.warehouseid || null,
                clientId: payload.clientid || null,
                orderdate: payload.orderdate || null,
                po: payload.po || '',
                notes: payload.notes || ''
              }
            }));
          }, 120);
          sendingGuardRef.current = false;
          return;
        }
        // Default/table reply
        const botMsg = data.table
          ? { text: data.reply || '', sender: 'bot', table: data.table }
          : { text: data.reply || 'OK', sender: 'bot' };
        if (data.csv) botMsg.csv = data.csv;
        if (data.json) botMsg.json = data.json;
        setMessages([...newMessages, botMsg]);
      } else {
        setMessages([...newMessages, { text: data.error || 'Error occurred', sender: 'bot' }]);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      setMessages([...newMessages, { text: 'Failed to connect to chat agent', sender: 'bot' }]);
    } finally {
      currentRequestControllerRef.current = null;
      setIsSending(false);
      sendingGuardRef.current = false;
    }
  }, [input, interim, userId, messages, navigate, isListening, clearSilenceTimer]);

  useEffect(() => { sendMessageRef.current = sendMessage; }, [sendMessage]);

  const stopCurrentRequest = useCallback(() => {
    if (!isSending) return;
    try { currentRequestControllerRef.current?.abort(); } catch {}
    currentRequestControllerRef.current = null;
    setIsSending(false);
    sendingGuardRef.current = false;
  }, [isSending]);

  /* ---------------------- Dictation: live typing + robust autosend ---------------------- */
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const mediaSupported = !!(window.MediaRecorder && navigator?.mediaDevices?.getUserMedia);
    setSpeechSupported(!!SR || mediaSupported);
  }, []);

  const appendCommittedText = useCallback((text) => {
    const add = String(text || '').trim();
    if (!add) return '';
    setInput(prev => (prev ? (prev.endsWith(' ') ? prev : `${prev} `) : '') + add);
    setTimeout(scrollInputToEnd, 0);
    return add;
  }, [scrollInputToEnd]);

  const transcribeRecordedAudio = useCallback(async (blob, { submitAfterStop = true } = {}) => {
    if (!blob || !blob.size || !userId) return;
    setIsTranscribing(true);
    setInterim('Transcribing…');
    try {
      const apiBase = import.meta.env.VITE_API_URL || '';
      const form = new FormData();
      const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
      form.append('audio', blob, `chat-agent-dictation.${ext}`);
      form.append('language', 'en');
      form.append('prompt', 'Transcribe verbatim. Preserve every spoken word exactly as spoken, including command phrases like "show me", "open", and client names. Do not summarize, rewrite, label, or clean up the wording.');

      const res = await fetch(`${apiBase}/api/agent/transcribe`, {
        method: 'POST',
        headers: { 'x-user-id': String(userId) },
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Transcription failed');
      }

      const text = String(data?.text || '').trim();
      setInterim('');
      if (!text) {
        showToast({ type: 'error', message: 'No speech was detected in the recording.', align: 'center', pill: true });
        return;
      }

      heardSpeechRef.current = true;
      const added = appendCommittedText(text);
      const base = inputStateRef.current || '';
      const composed = `${base}${added ? (base && !base.endsWith(' ') ? ' ' : '') + added : ''}`.trim();
      if (submitAfterStop && composed && sendMessageRef.current) {
        sendMessageRef.current(composed);
      }
    } catch (err) {
      console.warn('Audio transcription failed', err);
      setInterim('');
      showToast({ type: 'error', message: err?.message || 'Unable to transcribe audio.', align: 'center', pill: true });
    } finally {
      setIsTranscribing(false);
    }
  }, [appendCommittedText, userId]);

  const ensureMediaRecorderSupport = useCallback(() => {
    if (!window.MediaRecorder || !navigator?.mediaDevices?.getUserMedia) return null;
    return window.MediaRecorder;
  }, []);

  const chooseMediaRecorderMimeType = useCallback((MediaRecorderCtor) => {
    if (!MediaRecorderCtor?.isTypeSupported) return '';
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/ogg;codecs=opus',
      'audio/webm',
      'audio/ogg',
    ];
    return candidates.find((value) => MediaRecorderCtor.isTypeSupported(value)) || '';
  }, []);

  const startMediaDictation = useCallback(async () => {
    const MediaRecorderCtor = ensureMediaRecorderSupport();
    if (!MediaRecorderCtor) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stopMediaStream();
      mediaStreamRef.current = stream;
      mediaChunksRef.current = [];
      const mimeType = chooseMediaRecorderMimeType(MediaRecorderCtor);
      mediaMimeTypeRef.current = mimeType || 'audio/webm';
      const recorder = mimeType ? new MediaRecorderCtor(stream, { mimeType }) : new MediaRecorderCtor(stream);
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) mediaChunksRef.current.push(event.data);
      };
      recorder.onstart = () => {
        setInterim('');
        setIsListening(true);
        heardSpeechRef.current = false;
        sendingGuardRef.current = false;
        playTone('start');
        startMediaLevelMonitor(stream);
      };
      recorder.onerror = (event) => {
        console.warn('MediaRecorder error:', event?.error || event);
        setIsListening(false);
        clearSilenceTimer();
        stopMediaStream();
        setInterim('');
        showToast({ type: 'error', message: 'Voice dictation failed to record audio.', align: 'center', pill: true });
      };
      recorder.onstop = async () => {
        setIsListening(false);
        clearSilenceTimer();
        stopMediaStream();
        const blob = new Blob(mediaChunksRef.current, { type: mediaMimeTypeRef.current || 'audio/webm' });
        mediaRecorderRef.current = null;
        mediaChunksRef.current = [];
        playTone('stop');
        if (!blob.size) {
          setInterim('');
          return;
        }
        await transcribeRecordedAudio(blob, { submitAfterStop: submitAfterMediaStopRef.current !== false });
        submitAfterMediaStopRef.current = true;
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      return true;
    } catch (err) {
      console.warn('Media recorder start failed', err);
      stopMediaStream();
      setIsListening(false);
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        showToast({ type: 'error', message: 'Microphone permission was denied.', align: 'center', pill: true });
      } else {
        showToast({ type: 'error', message: 'Unable to start voice dictation.', align: 'center', pill: true });
      }
      return false;
    }
  }, [chooseMediaRecorderMimeType, clearSilenceTimer, ensureMediaRecorderSupport, playTone, startMediaLevelMonitor, stopMediaStream, transcribeRecordedAudio]);

  const ensureRecognizer = useCallback(() => {
    if (recognitionRef.current) return recognitionRef.current;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return null;
    const recog = new SR();
    recog.lang = 'en-US';
    recog.interimResults = true; // live words
    recog.continuous = true; // keep session alive; we'll stop on silence
    recog.onstart = () => {
      setInterim('');
      setIsListening(true);
      sendingGuardRef.current = false;
      heardSpeechRef.current = false; // reset speech flag
      playTone('start');
      // DO NOT arm silence timer here; wait until we actually hear something
    };
    recog.onerror = (e) => {
      console.warn('Speech error:', e.error);
      setIsListening(false);
      setInterim('');
      clearSilenceTimer();
      if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') {
        showToast({ type: 'error', message: 'Microphone permission was denied.', align: 'center', pill: true });
      } else if (e?.error) {
        showToast({ type: 'error', message: `Voice dictation failed: ${e.error}`, align: 'center', pill: true });
      }
    };
    recog.onresult = (event) => {
      let interimNow = '';
      let finalChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = (event.results[i][0]?.transcript || '');
        if (event.results[i].isFinal) finalChunk += transcript;
        else interimNow += transcript;
      }
      if (interimNow || finalChunk) {
        heardSpeechRef.current = true; // we heard real speech
        armSilenceTimer(); // re-arm silence on any activity
      }
      // show live preview
      setInterim(interimNow);
      // commit finalized chunk(s)
      if (finalChunk) {
        const add = finalChunk.trim();
        if (add) {
          setInput(prev => (prev ? (prev.endsWith(' ') ? prev : prev + ' ') : '') + add);
          setTimeout(scrollInputToEnd, 0);
        }
      }
    };
    // helpful signals, but not always fired in all environments
    recog.onsoundstart = () => { armSilenceTimer(); };
    recog.onsoundend = () => { armSilenceTimer(); };
    // When the engine ends (because we called stop() on silence)
    recog.onend = () => {
      setIsListening(false);
      clearSilenceTimer();
      if (autoStoppedDictationRef.current) {
        autoStoppedDictationRef.current = false;
        return;
      }
      const committed = inputStateRef.current || '';
      const preview = interimStateRef.current || '';
      const composed = `${committed}${preview ? (committed && !committed.endsWith(' ') ? ' ' : '') + preview : ''}`.trim();
      setInterim('');
      // Only auto-send if we actually heard speech and there's content
      if (heardSpeechRef.current && composed && !sendingGuardRef.current) {
        sendingGuardRef.current = true;
        if (sendMessageRef.current) sendMessageRef.current(composed);
      }
    };
    recognitionRef.current = recog;
    return recog;
  }, [armSilenceTimer, clearSilenceTimer, scrollInputToEnd]);

  const toggleDictation = useCallback(() => {
    const recog = ensureRecognizer();
    if (recog) {
      try {
        if (isListening) {
          clearSilenceTimer();
          autoStoppedDictationRef.current = false;
          setIsListening(false);
          playTone('stop');
          recog.stop();
        } else {
          setInterim('');
          sendingGuardRef.current = false;
          heardSpeechRef.current = false;
          autoStoppedDictationRef.current = false;
          recog.start();
        }
      } catch (err) {
        console.warn('Speech start/stop failed', err);
        setIsListening(false);
        clearSilenceTimer();
        showToast({ type: 'error', message: 'Unable to start voice dictation.', align: 'center', pill: true });
      }
      return;
    }

    if (mediaRecorderRef.current) {
      try {
        if (isListening) {
          submitAfterMediaStopRef.current = true;
          mediaRecorderRef.current.stop();
        } else {
          setInterim('');
          sendingGuardRef.current = false;
          heardSpeechRef.current = false;
          void startMediaDictation();
        }
      } catch (err) {
        console.warn('Media dictation start/stop failed', err);
        setIsListening(false);
        stopMediaStream();
        showToast({ type: 'error', message: 'Unable to start voice dictation.', align: 'center', pill: true });
      }
      return;
    }

    if (!speechSupported) {
      showToast({ type: 'error', message: 'Voice dictation is not supported in this browser.', align: 'center', pill: true });
      return;
    }

    setInterim('');
    sendingGuardRef.current = false;
    heardSpeechRef.current = false;
    void startMediaDictation();
  }, [ensureRecognizer, isListening, clearSilenceTimer, playTone, speechSupported, startMediaDictation, stopMediaStream]);

  /* ---------------------- Clear agent conversation ---------------------- */
  const clearInput = () => {
    setInterim('');
    setInput('');
    if (isListening && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      setIsListening(false);
    }
    if (isListening && mediaRecorderRef.current) {
      try {
        submitAfterMediaStopRef.current = false;
        mediaRecorderRef.current.stop();
      } catch {}
      setIsListening(false);
    }
  };

  const clearConversation = useCallback(() => {
    clearInput();
    setAnnouncement('');
    setMessages([]);
    showToast({ type: 'success', message: 'Assistant window cleared', align: 'center', pill: true });
  }, [clearInput]);

  const closeChat = useCallback(() => {
    setAnnouncement('');
    setAssistantNeedsAttention(false);
    setIsChatOpen(false);
  }, [setAssistantNeedsAttention, setIsChatOpen]);

  /* ---------------------- Drag / Resize ---------------------- */
  const startDragging = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.pageX, startY = e.pageY;
    const startLeft = position.x, startTop = position.y;
    const handleMouseMove = (e2) => {
      const newX = startLeft + (e2.pageX - startX);
      const newY = startTop + (e2.pageY - startY);
      const vpW = window.innerWidth || 1024, vpH = window.innerHeight || 768;
      const maxX = Math.max(0, vpW - size.width);
      const maxY = Math.max(0, vpH - size.height);
      setPosition({ x: clamp(newX, 0, maxX), y: clamp(newY, 0, maxY) });
    };
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { once: true });
  }, [position.x, position.y, size.width, size.height]);

  const startResizing = useCallback((direction, e) => {
    e.preventDefault();
    e.stopPropagation();
    try { document.body.classList.add('chat-resizing'); } catch {}
    const startX = e.pageX, startY = e.pageY;
    const startWidth = size.width, startHeight = size.height;
    const startLeft = position.x, startTop = position.y;
    const minW = 300, minH = 400, maxHCap = 1000;
    const handleMouseMove = (e2) => {
      const vpW = window.innerWidth || 1024, vpH = window.innerHeight || 768;
      const maxW = vpW;
      const maxH = Math.min(maxHCap, vpH);
      const deltaX = e2.pageX - startX;
      const deltaY = e2.pageY - startY;
      const fromLeft = direction.includes('left');
      const fromTop = direction.includes('top');

      let nextWidth = startWidth + (fromLeft ? -deltaX : deltaX);
      let nextHeight = startHeight + (fromTop ? -deltaY : deltaY);
      nextWidth = clamp(nextWidth, minW, maxW);
      nextHeight = clamp(nextHeight, minH, maxH);

      let nextX = fromLeft ? startLeft + (startWidth - nextWidth) : startLeft;
      let nextY = fromTop ? startTop + (startHeight - nextHeight) : startTop;

      nextX = clamp(nextX, 0, Math.max(0, vpW - nextWidth));
      nextY = clamp(nextY, 0, Math.max(0, vpH - nextHeight));

      if (fromLeft) {
        nextWidth = clamp(startLeft + startWidth - nextX, minW, maxW);
        nextX = clamp(startLeft + startWidth - nextWidth, 0, Math.max(0, vpW - nextWidth));
      }
      if (fromTop) {
        nextHeight = clamp(startTop + startHeight - nextY, minH, maxH);
        nextY = clamp(startTop + startHeight - nextHeight, 0, Math.max(0, vpH - nextHeight));
      }

      setSize({ width: nextWidth, height: nextHeight });
      setPosition({ x: nextX, y: nextY });
    };
    const handleMouseUp = () => {
      try { document.body.classList.remove('chat-resizing'); } catch {}
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { once: true });
  }, [position.x, position.y, size.width, size.height]);

  if (!shouldRender || isClient) return null;

  // Live value the user sees: committed + interim
  const composedInput = `${input}${interim ? (input && !input.endsWith(' ') ? ' ' : '') + interim : ''}`;
  const fallbackAnchor = {
    left: (window.innerWidth || 1280) - 88,
    top: 18,
    width: 36,
    height: 36,
  };
  const anchor = assistantAnchorRect || fallbackAnchor;
  const anchorCenterX = Number(anchor.left || 0) + (Number(anchor.width || 0) / 2);
  const anchorCenterY = Number(anchor.top || 0) + (Number(anchor.height || 0) / 2);
  const modalCenterX = position.x + (size.width / 2);
  const modalCenterY = position.y + (size.height / 2);
  const motionStyle = {
    '--chat-enter-x': `${anchorCenterX - modalCenterX}px`,
    '--chat-enter-y': `${anchorCenterY - modalCenterY}px`,
    '--chat-origin-x': `${anchorCenterX - position.x}px`,
    '--chat-origin-y': `${anchorCenterY - position.y}px`,
  };

  return (
    <div className={`chat-overlay ${isWindowVisible ? 'is-open' : 'is-closing'}`}>
      <div
        className={`chat-modal ${isWindowVisible ? 'is-open' : 'is-closing'}`}
        style={{
          ...motionStyle,
          width: `${size.width}px`,
          height: `${size.height}px`,
          left: `${position.x}px`,
          top: `${position.y}px`,
          position: 'absolute',
          zIndex: 2000,
        }}
      >
        <div className="chat-header" onMouseDown={startDragging}>
          <h3>Easy3PL Assistant</h3>
          <button className="chat-close" onClick={closeChat}>×</button>
        </div>
        <div className="chat-messages" ref={messagesRef}>
          {mode === 'agent' ? (
            <>
              {announcement && (
                <div className="chat-message bot announcement">
                  <span>{announcement}</span>
                </div>
              )}
              {messages.map((msg, index) => (
                <div key={index} className={`chat-message ${msg.sender}${msg.table ? ' has-table' : ''}`}>
                  {msg.table ? (<><div style={{ marginBottom: 6 }}>{msg.text}</div><TableView table={msg.table} /></>) : (<span>{msg.text}</span>)}
                </div>
              ))}
              {isSending && (
                <div className="chat-message bot pending" aria-live="polite">
                  <span>Thinking<span className="thinking-dots">...</span></span>
                </div>
              )}
            </>
          ) : null}
        </div>
        <div className="chat-input">
          <input
            ref={inputElRef}
            type="text"
            value={composedInput}
            onChange={(e) => {
              // If user edits while interim visible, treat entire new value as committed input
              setInput(e.target.value);
              setInterim('');
              setTimeout(scrollInputToEnd, 0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isSending && !isTranscribing) {
                // stop mic if running, then send composed text
                if (isListening && recognitionRef.current) {
                  try { recognitionRef.current.stop(); } catch {}
                }
                if (isListening && mediaRecorderRef.current) {
                  e.preventDefault();
                  try {
                    submitAfterMediaStopRef.current = true;
                    mediaRecorderRef.current.stop();
                  } catch {}
                  return;
                }
                sendMessage();
              }
            }}
            placeholder={'Speak or type your message…'}
          />
          {/* Dictation (Mic) toggle */}
          <button
            className={`btn-mic ${isListening ? 'listening' : ''}`}
            onClick={toggleDictation}
            title={speechSupported ? (isListening ? 'Stop dictation' : 'Start dictation') : 'Dictation not supported'}
            aria-pressed={isListening}
            aria-label="Dictate"
            disabled={isSending || isTranscribing}
          >
            {isListening ? <FaMicrophoneSlash /> : <FaMicrophone />}
          </button>
          {isSending ? (
            <button className="btn-stop" onClick={stopCurrentRequest}>Stop</button>
          ) : (
            <button onClick={() => sendMessage()} disabled={isTranscribing}>{isTranscribing ? 'Transcribing...' : 'Send'}</button>
          )}
          <button className="btn-clear" onClick={clearConversation}>Clear</button>
        </div>
        <div className="resize-handle resize-handle--top-left" onMouseDown={(e) => startResizing('top-left', e)} />
        <div className="resize-handle resize-handle--top-right" onMouseDown={(e) => startResizing('top-right', e)} />
        <div className="resize-handle resize-handle--bottom-left" onMouseDown={(e) => startResizing('bottom-left', e)} />
        <div className="resize-handle resize-handle--bottom-right" onMouseDown={(e) => startResizing('bottom-right', e)} />
      </div>
    </div>
  );
};

export default ChatAgent;
